export type LocalRole = "admin" | "sales";
export type MarketCoverageFilter = "all" | "none" | "one" | "2-4" | "5+";
export type MarketSort = "product" | "coverage" | "median" | "competitive" | "spread";
export interface MarketFilters {
  q: string; source: string; coverage: MarketCoverageFilter; sort: MarketSort;
  direction: "asc" | "desc"; page: number; pageSize: 25 | 50 | 100;
}
export interface MarketSource { key: string; name: string }
export interface MarketRun {
  id: number; sourceKey: string; startedAt: string; finishedAt: string | null;
  requested: number; found: number; notFound: number; errors: number;
}
export interface MarketProductRow {
  id: string; brand: string; mpn: string; description: string; cohort: string; ordinal: number;
  sourcePrices: Record<string, number | null>; medianEur: number | null;
  competitivePriceEur: number | null; sourcesFound: number; updatedAt: string | null;
  spreadPercent: number | null;
}
export interface MarketSourceReport extends MarketSource {
  confirmed: number; coveragePercent: number; checked: number; notFound: number;
  errors: number; unverified: number; lastCheckedAt: string | null; lastRun: MarketRun | null;
}
export interface MarketOverview {
  generatedAt: string; tableUpdatedAt: string | null;
  summary: { products: number; priced: number; unpriced: number; sources: number;
    confirmedQuotes: number; freshConfirmedQuotes: number; incompleteSources: number;
    totalRuns: number; disabledSources: number };
  coverage: { none: number; one: number; twoToFour: number; fivePlus: number };
  sources: MarketSourceReport[]; activeSources: MarketSource[]; recentRuns: MarketRun[];
  filters: MarketFilters; total: number; items: MarketProductRow[];
}
export interface MarketQuote {
  productId: string; sourceKey: string; priceEur: number | null; title: string | null;
  url: string | null; searchUrl: string | null; candidateCount: number | null;
  detailCandidatesChecked: number | null; status: string; verificationStatus: string | null;
  error: string | null; checkedAt: string | null;
}
export interface MarketProductDetail extends MarketProductRow {
  tableUpdatedAt: string | null;
  sources: Array<MarketSource & { priceEur: number | null; quote: MarketQuote | null }>;
}
export type SalesSort = "score" | "profitability" | "condition" | "added" | "source" | "title";
export type SortDirection = "asc" | "desc";
export type PriceTypeFilter = "fixed" | "negotiable" | "unknown";
export type PriceScopeFilter = "item" | "lot";
export const CONDITION_VALUES = ["new", "open_box", "refurbished", "used", "for_parts", "unknown"] as const;
export type ConditionFilter = typeof CONDITION_VALUES[number];
export type ScoreBandFilter = "100" | "87-99" | "70-86" | "55-69" | "1-54";
export type SalesPeriod = "day" | "yesterday" | "week" | "range" | "all";
export type PriceView = "regular" | "off50";
export type SalesStatus = 0 | 1 | 2 | 3 | null;

export interface SalesListing {
  key: string;
  source: string;
  sourceDatabase: string;
  listingId: string;
  title: string | null;
  url: string | null;
  priceEur: number | null;
  comparisonPriceEur: number | null;
  vatBasis: string | null;
  referencePriceEur: number | null;
  priceType: string | null;
  priceScope: string | null;
  quantity: number | null;
  condition: string | null;
  mpns: string[];
  score: number | null;
  salesStatus: SalesStatus;
  availabilityStatus: string | null;
  lastConfirmedAt: string | null;
  priceChangedAt: string | null;
  capturedAt: string | null;
  addedAt: string | null;
}

export interface SnapshotListing extends SalesListing {
  hasMultipleMpns: boolean;
  matchedMpnsRaw: string | null;
}

export interface SalesCatalogSnapshot {
  version: number;
  checksum: string;
  completedAt: string | null;
  rowCount: number;
  items: SnapshotListing[];
}

export interface SalesCatalogManifest extends Omit<SalesCatalogSnapshot, 'items'> {
  chunkCount: number;
}

export interface SalesStatusRecord {
  sourceDatabase: string;
  listingId: string;
  status: Exclude<SalesStatus, null>;
}

export interface CatalogListing extends SalesListing {
  recheck?: { status: "gone"; observedAt: string | null };
}

export interface SalesFiltersState {
  q: string;
  period: SalesPeriod;
  from: string;
  to: string;
  sources: string[] | null;
  priceTypes: PriceTypeFilter[] | null;
  priceScopes: PriceScopeFilter[] | null;
  conditions: ConditionFilter[] | null;
  priceView: PriceView;
  scoreBands: ScoreBandFilter[] | null;
  sort: SalesSort;
  direction: SortDirection;
  page: number;
  pageSize: 25 | 50 | 100;
}

export interface CatalogProduct {
  id: string; brand: string; mpn: string; description: string; cohort: string;
  categoryId: string | null; batchIds: string[]; ordinal: number;
  listingsCount: number;
}
export interface CatalogOverview { total: number; withListings: number; items: CatalogProduct[] }
export interface CatalogProductDetail extends Omit<CatalogProduct, "listingsCount"> { listings: CatalogListing[] }

export interface SalesFilterOptions {
  sources: string[];
  priceTypes: PriceTypeFilter[];
  priceScopes: PriceScopeFilter[];
  conditions: ConditionFilter[];
  scoreBands: ScoreBandFilter[];
}

export interface SalesResponse {
  items: SalesListing[];
  total: number;
  page: number;
  pageSize: number;
  summary: {
    total: number;
    sources: number;
    score100: number;
    multipleMpns: number;
  };
  filters: SalesFilterOptions;
  sync?: {
    completedAt: string | null;
    rowCount: number;
  };
}

export type SourceDataStatus = "processed" | "pending" | "errors" | "no-results" | "empty" | "unavailable";
export type SourceStageKey = "primary" | "details" | "normalization" | "detailMatching" | "codex" | "scoring";

export interface SourceStageProgress {
  completed: number;
  failed: number;
  total: number;
}

export interface SourceOverview {
  id: string;
  name: string;
  domain: string;
  url: string;
  status: SourceDataStatus;
  observed: number;
  pending: number;
  results: number;
  published: number;
  errors: number;
  lastActivityAt: string | null;
  databaseUpdatedAt: string | null;
  stages: Record<SourceStageKey, SourceStageProgress>;
  run?: SourceRun | null;
  periodTotals?: SourceRunTotals | null;
}

export type LeadView = 'gone' | 'changed' | 'available';
export interface LeadListing {
  key: string; source: string; listingId: string; url: string | null; title: string | null;
  pageStatus: string; stockStatus: string; result: string | null;
  checkedAt: string | null; confirmedAt: string | null;
  previousAmountMinor: number | null; amountMinor: number | null;
  currency: string | null; priceChangedAt: string | null;
}
export interface LeadOverview {
  generatedAt: string; publicationEnabled: boolean;
  summary: { total: number; available: number; gone: number; outOfStock: number;
    unverified: number; stockUnknown: number; priceChanged: number; lastAttemptedAt: string | null };
  view: LeadView; total: number; page: number; pageSize: number; items: LeadListing[];
}
export interface QuantityExamples {
  generatedAt: string;
  items: Array<{ source: string; listingId: string; title: string; url: string | null;
    quantity: number; evidence: string; bindingStatus: string }>;
  estimates: Array<{ source: string; listingId: string; quantity: 1;
    method: 'inferred_single_listing' | 'source_card_unit' | 'description_explicit'; evidence?: string }>;
}

export type SourcePeriodMode = "last" | "24h" | "range";
export interface SourcePeriod { mode: SourcePeriodMode; from: string | null; to: string | null }
export type SourceRunStatus = "complete" | "partial" | "blocked" | "failed" | "running";
export interface SourceRunFunnel {
  newIds: number;
  afterPrimary: number;
  detailMatching: number;
  detailConfirmed: number;
  codexRequired: number;
  codexMatching: number;
  published: number;
}
export interface SourceRunTotals {
  observed: number;
  newListings: number;
  primaryProcessed: number;
  movedToMatches: number;
  detailCompleted: number;
  detailUnavailable: number;
  detailFailed: number;
  scoringCompleted: number;
  scoringFailed: number;
  publishedAdded: number;
  publishedUpdated: number;
  publishedRemoved: number;
  runs?: number;
  funnel?: SourceRunFunnel | null;
}
export interface SourceRun extends SourceRunTotals {
  id: string;
  groupId: string;
  source: string;
  attempt: number;
  startedAt: string;
  finishedAt: string | null;
  status: SourceRunStatus;
  stage: string | null;
  reasonCode: string | null;
  reason: string | null;
  stop: string | null;
  complete: boolean | null;
  pages: number | null;
  maxPages: number | null;
  expectedPages: number | null;
  sheetStatus: string | null;
  funnel: SourceRunFunnel | null;
  routes: Array<{ route: string; pages: number; expectedPages?: number; complete?: boolean }>;
}
export interface SourceTrendPoint {
  day: string; runs: number; newListings: number; detailCompleted: number;
  scoringCompleted: number; publishedAdded: number; blocked: number; partial: number;
}
export interface SourceRetainedActivity {
  available: boolean;
  totals: { captured: number; detailAttempted: number; scored: number; publishedStillPresent: number };
  bySource: Record<string, SourceRetainedActivity["totals"]>;
  days: Array<{ day: string } & SourceRetainedActivity["totals"]>;
}

export interface SourcesResponse {
  compact?: boolean;
  items: SourceOverview[];
  generatedAt: string;
  summary: {
    sources: number;
    observed: number;
    pending: number;
    results: number;
    published: number;
    issues: number;
  };
  period: SourcePeriod;
  historyAvailable: boolean;
  retained: SourceRetainedActivity;
  activity: SourceRunTotals & {
    funnel: SourceRunFunnel | null;
    previousFunnel: SourceRunFunnel | null;
    previous: SourceRunTotals;
    hasPrevious: boolean;
    statuses: Record<SourceRunStatus, number>;
    sourcesRun: number;
  };
  trend: SourceTrendPoint[];
}

export interface SourceDetailResponse {
  compact?: boolean;
  source: SourceOverview;
  period: SourcePeriod;
  historyAvailable: boolean;
  retained: SourceRetainedActivity;
  latestRun: SourceRun | null;
  runs: SourceRun[];
  recentRuns: SourceRun[];
  totals: SourceRunTotals;
  previousTotals: SourceRunTotals;
  funnel: SourceRunFunnel | null;
  previousFunnel: SourceRunFunnel | null;
  hasPrevious: boolean;
  trend: SourceTrendPoint[];
  breakdown: { matched: number; scored: number; scorePositive: number; detailUnavailable: number; detailFailed: number };
}

export type SourceListingState = "published" | "unavailable" | "error" | "not_published" | "processing" | "pending" | "seen";
export interface SourceListingSummary {
  id: string; title: string | null; url: string | null; capturedAt: string | null;
  detailStatus: string | null; scoreStatus: string | null; score: number | null;
  state: SourceListingState; reason: string | null;
}
export interface SourceListingsResponse { items: SourceListingSummary[]; total: number; page: number; pageSize: number }
export interface SourceListingEvent { at: string | null; stage: string; status: string; reason: string | null; runId?: string }
export interface SourceListingDetail {
  source: { id: string; name: string; published: number };
  id: string; title: string | null; url: string | null; capturedAt: string | null;
  state: SourceListingState; published: boolean; publishedAt: string | null;
  score: number | null; reason: string | null; events: SourceListingEvent[];
}

export interface AccessContext {
  authenticated: true;
  user: { username: string; role: LocalRole };
  permissions: string[];
  navigation: Array<{ key: string; label: string; href: string; permission: string; planned?: boolean }>;
}

export type SessionResponse = AccessContext | { authenticated: false };
